import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { contentService } from '../../../services/contentService';

const ContentLibrary = ({ 
  contentItems, 
  selectedItems, 
  onSelectItem, 
  onSelectAll, 
  onItemClick,
  filters,
  onFilterChange,
  onContentUpdate
}) => {
  const [viewMode, setViewMode] = useState('grid');
  const [updatingItems, setUpdatingItems] = useState({});

  const handleToggleFeatured = async (itemId, currentFeatured) => {
    try {
      setUpdatingItems(prev => ({ ...prev, [itemId]: true }));
      await contentService?.update(itemId, { isFeatured: !currentFeatured });
      if (onContentUpdate) {
        await onContentUpdate();
      }
    } catch (error) {
      console.error('Error toggling featured:', error);
    } finally {
      setUpdatingItems(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const handleRatingChange = async (itemId, newRating) => {
    try {
      setUpdatingItems(prev => ({ ...prev, [itemId]: true }));
      await contentService?.update(itemId, { rating: newRating });
      if (onContentUpdate) {
        await onContentUpdate();
      }
    } catch (error) {
      console.error('Error updating rating:', error);
    } finally {
      setUpdatingItems(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const filteredItems = contentItems?.filter(item => {
    if (filters?.category && filters?.category !== 'all' && item?.category !== filters?.category) {
      return false;
    }
    if (filters?.type && filters?.type !== 'all' && item?.type !== filters?.type) {
      return false;
    }
    if (filters?.search) {
      const searchLower = filters?.search?.toLowerCase();
      return item?.title?.toLowerCase()?.includes(searchLower) || 
             item?.description?.toLowerCase()?.includes(searchLower);
    }
    return true;
  });

  const allSelected = filteredItems?.length > 0 && 
    filteredItems?.every(item => selectedItems?.includes(item?.id));

  return (
    <div className="flex flex-col h-full bg-card rounded-lg glow-primary">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center glow-primary">
              <Icon name="FolderOpen" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-heading font-bold text-primary">
                Biblioteca de Conteúdo
              </h2>
              <p className="text-xs md:text-sm text-muted-foreground">
                {filteredItems?.length} itens encontrados
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="sm"
              iconName="Grid"
              onClick={() => setViewMode('grid')}
            >
              Grade
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              iconName="List"
              onClick={() => setViewMode('list')}
            >
              Lista
            </Button>
          </div>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Icon 
              name="Search" 
              size={18} 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" 
            />
            <input
              type="text"
              placeholder="Buscar por título ou descrição..."
              value={filters?.search}
              onChange={(e) => onFilterChange('search', e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth"
            />
          </div>

          <select
            value={filters?.category}
            onChange={(e) => onFilterChange('category', e?.target?.value)}
            className="px-4 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth"
          >
            <option value="all">Todas Categorias</option>
            <option value="movies-series">Filmes e Séries</option>
            <option value="bbb">Big Brother Brasil</option>
            <option value="brasileirao">Brasileirão</option>
            <option value="app-partnerships">Parcerias de Apps</option>
          </select>

          <select
            value={filters?.type}
            onChange={(e) => onFilterChange('type', e?.target?.value)}
            className="px-4 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth"
          >
            <option value="all">Todos Tipos</option>
            <option value="image">Imagens</option>
            <option value="video">Vídeos</option>
          </select>
        </div>

        {selectedItems?.length > 0 && (
          <div className="mt-4 p-3 bg-primary/10 rounded-md flex items-center justify-between">
            <span className="text-sm font-medium text-primary">
              {selectedItems?.length} item(ns) selecionado(s)
            </span>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={() => onSelectAll(false)}
            >
              Limpar
            </Button>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto custom-scrollbar p-4 md:p-6">
        {filteredItems?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-12">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Icon name="Search" size={32} color="var(--color-muted-foreground)" />
            </div>
            <h3 className="text-lg font-heading font-bold text-foreground mb-2">
              Nenhum conteúdo encontrado
            </h3>
            <p className="text-sm text-muted-foreground max-w-md">
              Tente ajustar seus filtros ou faça upload de novo conteúdo
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredItems?.map((item) => (
              <div
                key={item?.id}
                className="group relative bg-background rounded-lg overflow-hidden border border-border hover:border-primary transition-smooth cursor-pointer"
                onClick={() => onItemClick(item)}
              >
                <div className="absolute top-2 left-2 z-10">
                  <input
                    type="checkbox"
                    checked={selectedItems?.includes(item?.id)}
                    onChange={(e) => {
                      e?.stopPropagation();
                      onSelectItem(item?.id);
                    }}
                    className="w-5 h-5 rounded border-2 border-primary bg-background checked:bg-primary cursor-pointer"
                  />
                </div>

                <div className="aspect-[4/3] relative overflow-hidden">
                  {item?.type === 'video' ? (
                    <div className="absolute inset-0 bg-muted flex items-center justify-center">
                      {item?.videoUrl ? (
                        <>
                          <video 
                            src={item?.videoUrl} 
                            className="w-full h-full object-cover"
                            preload="metadata"
                            controls
                            playsInline
                          />
                        </>
                      ) : (
                        <Icon name="Play" size={48} color="var(--color-primary)" />
                      )}
                    </div>
                  ) : (
                    <Image
                      src={item?.thumbnail}
                      alt={item?.thumbnailAlt}
                      className="w-full h-full object-cover group-hover:scale-105 transition-smooth"
                    />
                  )}
                  
                  {item?.watermark && (
                    <div className="absolute bottom-2 right-2 px-2 py-1 bg-background/80 rounded text-xs text-primary font-medium">
                      Marca d'água
                    </div>
                  )}
                  <div className="absolute top-2 right-2 flex gap-2">
                    {item?.format && item?.format !== 'custom' && (
                      <span className="px-2 py-1 bg-accent/90 text-background text-xs font-bold rounded backdrop-blur-sm">
                        {item?.format === 'story' && '📱 Story'}
                        {item?.format === 'post' && '⬜ Post'}
                        {item?.format === 'landscape' && '🖼️ Landscape'}
                        {item?.format === 'portrait' && '📐 Portrait'}
                      </span>
                    )}
                    {item?.watermark && (
                      <span className="px-2 py-1 bg-primary/90 text-background text-xs font-bold rounded backdrop-blur-sm">
                        🔒
                      </span>
                    )}
                  </div>
                </div>

                <div className="p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      item?.type === 'video' ?'bg-secondary/20 text-secondary' :'bg-primary/20 text-primary'
                    }`}>
                      {item?.type === 'video' ? 'Vídeo' : 'Imagem'}
                    </span>
                    <button
                      onClick={(e) => {
                        e?.stopPropagation();
                        handleToggleFeatured(item?.id, item?.isFeatured);
                      }}
                      disabled={updatingItems?.[item?.id]}
                      className={`px-2 py-1 rounded text-xs font-medium transition-smooth ${
                        item?.isFeatured
                          ? 'bg-accent/90 text-white' :'bg-muted text-muted-foreground hover:bg-accent/20'
                      }`}
                    >
                      {item?.isFeatured ? '⭐ Destaque' : 'Destacar'}
                    </button>
                  </div>
                  
                  <h3 className="text-sm font-medium text-foreground line-clamp-1 mb-1">
                    {item?.title}
                  </h3>
                  <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                    {item?.description}
                  </p>
                  
                  <div className="flex items-center gap-1 mb-2">
                    {[1, 2, 3, 4, 5]?.map((star) => (
                      <button
                        key={star}
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleRatingChange(item?.id, star);
                        }}
                        disabled={updatingItems?.[item?.id]}
                        className="transition-smooth hover:scale-110"
                      >
                        <Icon
                          name="Star"
                          size={14}
                          color={star <= (item?.rating || 0) ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                          className={star <= (item?.rating || 0) ? 'fill-primary' : ''}
                        />
                      </button>
                    ))}
                    <span className="text-xs text-muted-foreground ml-1">
                      {(item?.rating || 0)?.toFixed(1)}
                    </span>
                  </div>
                  
                  <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                    <span>Ordem: {item?.displayOrder}</span>
                    <span>{new Date(item.uploadDate)?.toLocaleDateString('pt-BR')}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredItems?.map((item) => (
              <div
                key={item?.id}
                className="flex items-center gap-4 p-4 bg-background rounded-lg border border-border hover:border-primary transition-smooth cursor-pointer"
                onClick={() => onItemClick(item)}
              >
                <input
                  type="checkbox"
                  checked={selectedItems?.includes(item?.id)}
                  onChange={(e) => {
                    e?.stopPropagation();
                    onSelectItem(item?.id);
                  }}
                  className="w-5 h-5 rounded border-2 border-primary bg-background checked:bg-primary cursor-pointer flex-shrink-0"
                />

                <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0">
                  {item?.type === 'video' ? (
                    <div className="w-full h-full bg-muted flex items-center justify-center relative">
                      {item?.videoUrl ? (
                        <>
                          <video 
                            src={item?.videoUrl} 
                            className="w-full h-full object-cover"
                            preload="metadata"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <Icon name="Play" size={24} color="var(--color-primary)" />
                          </div>
                        </>
                      ) : (
                        <Icon name="Play" size={24} color="var(--color-primary)" />
                      )}
                    </div>
                  ) : (
                    <Image
                      src={item?.thumbnail}
                      alt={item?.thumbnailAlt}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-medium text-foreground line-clamp-1">
                      {item?.title}
                    </h3>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
                      item?.type === 'video' ?'bg-secondary/20 text-secondary' :'bg-primary/20 text-primary'
                    }`}>
                      {item?.type === 'video' ? 'Vídeo' : 'Imagem'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-1 mb-2">
                    {item?.description}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Icon name="Star" size={12} color="var(--color-primary)" />
                      {item?.rating}
                    </span>
                    <span>Ordem: {item?.displayOrder}</span>
                    <span>{new Date(item.uploadDate)?.toLocaleDateString('pt-BR')}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentLibrary;